import React from 'react';
import { PERCENTILE_LINES, Z_LINES } from '../../modules/growth/growthLms';
import { referenceCurves } from '../../modules/growth/growthIndicators';

/**
 * Gráfico de uma curva de crescimento em SVG puro (só traços e texto, sem
 * imagem), para funcionar igual na tela e no PDF (html2canvas). As curvas
 * de referência vêm do LMS da OMS; os pontos são as medições do paciente.
 *
 * `points`: `[{ x: idade em meses, y: valor, implausible }]` já dentro da janela.
 * `markers`: idades (meses) das medições cranianas, marcadas no eixo.
 */

const WIDTH = 640;
const MARGIN = { top: 14, right: 46, bottom: 38, left: 46 };

const LINE_COLORS = { median: '#38A169', inner: '#ED8936', outer: '#E53E3E' };
const POINT_COLOR = '#5A80FB';
const PATH_COLOR = '#1A2559';
const MARKER_COLOR = '#805AD5';
const GRID_COLOR = '#EDF2F7';
const TEXT_COLOR = '#718096';

const niceStep = (span, count) => {
    const raw = span / count;
    const power = Math.pow(10, Math.floor(Math.log10(raw)));
    const unit = [1, 2, 2.5, 5, 10].find((candidate) => candidate * power >= raw) || 10;
    return unit * power;
};

const yTicks = (min, max) => {
    const step = niceStep(max - min, 5);
    const ticks = [];
    for (let value = Math.ceil(min / step) * step; value <= max + 1e-9; value += step) ticks.push(Number(value.toFixed(6)));
    return ticks;
};

const xTicks = ([from, to]) => {
    const step = to - from <= 24 ? 3 : 12;
    const ticks = [];
    for (let value = Math.ceil(from / step) * step; value <= to; value += step) ticks.push(value);
    return ticks;
};

const formatTick = (value) => String(Number(value.toFixed(1))).replace('.', ',');

const GrowthChart = ({ indicator, sex, mode = 'percentile', window, points = [], markers = [], height = 300 }) => {
    const lines = mode === 'z' ? Z_LINES : PERCENTILE_LINES;
    const segments = referenceCurves(indicator, sex, window, lines);
    if (segments.length === 0) return null;

    const values = segments.flatMap((segment) => segment.lines.flatMap((line) => line.points.map(([, y]) => y)))
        .concat(points.map((point) => point.y));
    const pad = (Math.max(...values) - Math.min(...values)) * 0.05;
    const yMin = Math.max(0, Math.min(...values) - pad);
    const yMax = Math.max(...values) + pad;
    const [from, to] = window;

    const plotWidth = WIDTH - MARGIN.left - MARGIN.right;
    const plotHeight = height - MARGIN.top - MARGIN.bottom;
    const sx = (x) => MARGIN.left + ((x - from) / (to - from)) * plotWidth;
    const sy = (y) => MARGIN.top + (1 - (y - yMin) / (yMax - yMin)) * plotHeight;
    const path = (list) => list.map(([x, y], index) => `${index ? 'L' : 'M'}${sx(x).toFixed(1)} ${sy(y).toFixed(1)}`).join(' ');

    const inYears = to - from > 24;
    const lastSegment = segments[segments.length - 1];
    const sorted = [...points].sort((a, b) => a.x - b.x);

    return (
        <svg viewBox={`0 0 ${WIDTH} ${height}`} width="100%" role="img" aria-label={indicator.label} style={{ display: 'block' }}>
            {yTicks(yMin, yMax).map((tick) => (
                <g key={`y${tick}`}>
                    <line x1={MARGIN.left} x2={WIDTH - MARGIN.right} y1={sy(tick)} y2={sy(tick)} stroke={GRID_COLOR} strokeWidth="1" />
                    <text x={MARGIN.left - 6} y={sy(tick) + 3.5} textAnchor="end" fontSize="10" fill={TEXT_COLOR}>{formatTick(tick)}</text>
                </g>
            ))}
            {xTicks(window).map((tick) => (
                <g key={`x${tick}`}>
                    <line x1={sx(tick)} x2={sx(tick)} y1={MARGIN.top} y2={MARGIN.top + plotHeight} stroke={GRID_COLOR} strokeWidth="1" />
                    <text x={sx(tick)} y={MARGIN.top + plotHeight + 14} textAnchor="middle" fontSize="10" fill={TEXT_COLOR}>
                        {inYears ? tick / 12 : tick}
                    </text>
                </g>
            ))}
            <text x={MARGIN.left + plotWidth / 2} y={height - 6} textAnchor="middle" fontSize="10.5" fill={TEXT_COLOR}>
                {inYears ? 'Idade (anos)' : 'Idade (meses)'}
            </text>
            <text x={12} y={MARGIN.top + plotHeight / 2} textAnchor="middle" fontSize="10.5" fill={TEXT_COLOR}
                transform={`rotate(-90 12 ${MARGIN.top + plotHeight / 2})`}>
                {`${indicator.short} (${indicator.unit})`}
            </text>

            {segments.map((segment, segmentIndex) => segment.lines.map((line) => {
                const kind = line.kind;
                return (
                    <path
                        key={`${segmentIndex}-${line.label}`}
                        d={path(line.points)}
                        fill="none"
                        stroke={LINE_COLORS[kind]}
                        strokeWidth={kind === 'median' ? 2 : 1.3}
                        strokeDasharray={kind === 'outer' ? '5 3' : undefined}
                    />
                );
            }))}
            {lastSegment.lines.map((line) => {
                const [x, y] = line.points[line.points.length - 1];
                return (
                    <text key={`label-${line.label}`} x={sx(x) + 4} y={sy(y) + 3.5} fontSize="10" fontWeight="600" fill={LINE_COLORS[line.kind]}>
                        {line.label}
                    </text>
                );
            })}

            {markers.filter((x) => x >= from && x <= to).map((x, index) => {
                const cx = sx(x);
                const base = MARGIN.top + plotHeight;
                return <path key={`m${index}`} d={`M${cx - 4} ${base} L${cx + 4} ${base} L${cx} ${base - 7} Z`} fill={MARKER_COLOR} opacity="0.85" />;
            })}

            {sorted.length > 1 && <path d={path(sorted.map((point) => [point.x, point.y]))} fill="none" stroke={PATH_COLOR} strokeWidth="1.4" />}
            {sorted.map((point, index) => (
                <circle
                    key={`p${index}`}
                    cx={sx(point.x)}
                    cy={sy(point.y)}
                    r="4"
                    fill={point.implausible ? LINE_COLORS.outer : POINT_COLOR}
                    stroke="#FFFFFF"
                    strokeWidth="1.5"
                />
            ))}
        </svg>
    );
};

export default GrowthChart;
